/**
 * Individual options
 */
export interface AioOption {
  /** The optional name for a newly created item (when adding). */
  optionName?: AioNewItem;
  /** The data type of the option. */
  type: AioOptionType;
  /** The current value held by the option. */
  value: unknown;
  /** Optional display label for the option. */
  label?: string;
  /** Allowed values list used for select-type options. */
  availableValues?: string[];
  /** When true, the option cannot be modified by the user. */
  readOnly?: boolean;
}

/**
 * Text replacements, and their replacement matrix
 */
export interface AioReplacement<T> {
  /** Unique identifier for this replacement entry. */
  airid: string;
  /** The text that should be replaced. */
  oldText: string;
  /** One or more sets of replacement values for this entry. */
  newTexts: AioReplacementValues<T>[];
  /** When true, include trailing characters in the replacement match. */
  includeTrailing?: boolean;
  /** Optional external name used to reference this replacement from outside. */
  externalName?: string;
}

/**
 * Type guard to check whether a value is an AioReplacement<T>.
 *
 * @template T The expected type of the replacement texts.
 * @param obj - The value to check.
 * @param chkT - A type guard used to validate the type of texts inside the replacement.
 * @returns True when obj conforms to AioReplacement<T>, otherwise false.
 */
export const isAioReplacement = <T>(
  obj: unknown,
  chkT: (o: unknown) => o is T,
): obj is AioReplacement<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check basic props
  const chkObj = obj as AioReplacement<T>;
  if (typeof chkObj.airid !== "string") return false;
  if (typeof chkObj.oldText !== "string") return false;
  if (
    !Array.isArray(chkObj.newTexts) ||
    chkObj.newTexts.some((o) => !isAioReplacementValues(o, chkT))
  )
    return false;
  // Check optional props
  if (chkObj.includeTrailing !== undefined && typeof chkObj.includeTrailing !== "boolean")
    return false;
  if (chkObj.externalName !== undefined && typeof chkObj.externalName !== "string") return false;

  // Everything is good
  return true;
};

/**
 * Replacement values for a text replacement entry.
 */
export interface AioReplacementValues<T> {
  /** Unique identifier for this replacement values entry. */
  airid: string;
  /** The replacement text values (one or more) of type T. */
  texts: T[];
  /** When true, append a space after the replacement when applied. */
  spaceAfter?: boolean;
  /** Optional nested replacement lists for hierarchical replacements. */
  subLists?: AioReplacement<T>[];
}

/**
 * Checks if an object is a AioReplacementValues.
 *
 * @param obj - The object to check.
 * @param chkT - A type guard function to check the type of the texts.
 * @returns boolean
 */
export const isAioReplacementValues = <T>(
  obj: unknown,
  chkT: (o: unknown) => o is T,
): obj is AioReplacementValues<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check basic props
  const chkObj = obj as AioReplacementValues<T>;
  if (typeof chkObj.airid !== "string") return false;
  // texts must be an array and none of the entries should fail the chkT guard
  if (!Array.isArray(chkObj.texts) || chkObj.texts.some((t) => !chkT(t))) return false;

  // Check optional props
  if (chkObj.spaceAfter !== undefined && typeof chkObj.spaceAfter !== "boolean") return false;
  if (
    chkObj.subLists !== undefined &&
    (!Array.isArray(chkObj.subLists) || chkObj.subLists.some((s) => !isAioReplacement(s, chkT)))
  )
    return false;

  // Everything is good
  return true;
};

/**
 * External replacements for a given name
 */
export interface AioExternalReplacements<T> {
  /** Name for the external replacement */
  givenName: string;
  /** Replacement text options */
  newTexts: AioReplacementValues<T>[];
}

/**
 * Type guard to check whether a value is an AioExternalReplacements<T>.
 *
 * @param obj - The object to check
 * @param isT - Type guard for type T
 * @returns boolean
 */
export const isAioExternalReplacements = <T>(
  obj: unknown,
  isT: (o: unknown) => o is T,
): obj is AioExternalReplacements<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;
  // Check lower interface
  const chkObj = obj as AioExternalReplacements<T>;
  if (typeof chkObj.givenName !== "string") return false;
  if (
    !Array.isArray(chkObj.newTexts) ||
    chkObj.newTexts.some((o) => !isAioReplacementValues(o, isT))
  )
    return false;
  // Everything is good
  return true;
};

/**
 * Single external replacement entry
 */
export interface AioExternalSingle<T> {
  /** Id for the external single replacement */
  airid: string;
  /** Text to be replaced */
  oldText: string;
  /** Replacement text */
  newText: T;
}

/**
 * Type guard to check whether a value is an AioExternalSingle<T>.
 *
 * @param obj - The object to check
 * @param isT - Type guard function for type T
 * @returns Whether the object is an AioExternalSingle<T>
 */
export const isAioExternalSingle = <T>(
  obj: unknown,
  isT: (o: unknown) => o is T,
): obj is AioExternalSingle<T> => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check basic props
  const chkObj = obj as AioExternalSingle<T>;
  if (typeof chkObj.airid !== "string") return false;
  if (typeof chkObj.oldText !== "string") return false;

  // Check generic type
  if (!isT(chkObj.newText)) return false;

  // Everything is good
  return true;
};

export enum AioOptionType {
  string = "string",
  number = "number",
  array = "array",
  object = "object",
  boolean = "boolean",
  select = "select",
  replacements = "replacements",
}

export enum AioNewItem {
  newKey = "newKey",
  newType = "newType",
}
